import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import { blogTranslations } from '../../translations/blogTranslations';
import { useLanguage } from '../../contexts/LanguageContext';
import { BlogPost as BlogPostType } from '../../translations/blogTranslations';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [readingProgress, setReadingProgress] = useState(0);

  // Dynamically generate blog content from translations with proper typing
  const blogContent = React.useMemo(() => {
    const posts = blogTranslations[language].blog.posts;
    return Object.entries(posts).reduce<Record<string, BlogPostType>>((acc, [key, post]) => ({
      ...acc,
      [key]: {
        title: post.title,
        date: post.date,
        readingTime: post.readingTime,
        metaDescription: post.metaDescription,
        content: post.content
      }
    }), {});
  }, [language]);

  const post = blogContent[slug as keyof typeof blogContent];

  // Get related posts (3 random posts excluding current)
  const relatedPosts = React.useMemo(() => {
    const allPosts = Object.entries(blogContent)
      .filter(([postSlug]) => postSlug !== slug)
      .map(([postSlug, postData]) => ({ slug: postSlug, ...postData }));
    
    return allPosts.sort(() => Math.random() - 0.5).slice(0, 3);
  }, [blogContent, slug]);

  useEffect(() => {
    if (!post) {
      navigate(`/${language}/blog`, { replace: true });
      return;
    }

    // Enhanced meta tags for SEO
    document.title = `${post.title} | Invoice-Converter.com`;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', post.metaDescription);
    }
    
    // Add article specific meta tags
    const metaTags = [
      { property: 'og:title', content: post.title },
      { property: 'og:description', content: post.metaDescription },
      { property: 'og:type', content: 'article' },
      { property: 'article:published_time', content: post.date },
      { name: 'twitter:title', content: post.title },
      { name: 'twitter:description', content: post.metaDescription }
    ];

    metaTags.forEach(tag => {
      let element = document.querySelector(`meta[${Object.keys(tag)[0]}="${Object.values(tag)[0]}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(Object.keys(tag)[0], Object.values(tag)[0]);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    // Reading progress indicator
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setReadingProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post, navigate, language]);

  if (!post) {
    return null;
  }

  const isGermanMandateArticle = slug?.includes('b2b-einvoicing') || slug?.includes('2025');

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-[var(--bg-secondary)] z-50">
        <div 
          className="h-full bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-hover)] transition-all duration-200"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-12 pt-24">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`/${language}/`} className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)]">
                {language === 'en' ? 'Home' : 'Startseite'}
              </Link>
            </li>
            <li className="text-[var(--text-tertiary)]">/</li>
            <li>
              <Link to={`/${language}/blog`} className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)]">
                Blog
              </Link>
            </li>
            <li className="text-[var(--text-tertiary)]">/</li>
            <li className="text-[var(--brand-primary)] font-medium truncate max-w-xs">
              {post.title}
            </li>
          </ol>
        </nav>

        {/* Article Header */}
        <article>
          <header className="mb-10">
            {isGermanMandateArticle && (
              <div className="inline-flex items-center bg-red-100 text-red-800 text-sm px-3 py-1 rounded-full mb-4">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                {language === 'en' ? 'Time-Sensitive: 2025 Mandate' : 'Zeitkritisch: 2025 Mandat'}
              </div>
            )}
            
            <h1 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-6 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-[var(--text-tertiary)]">
              <time dateTime={post.date} className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {new Date(post.date).toLocaleDateString(
                  blogTranslations[language].blog.locale,
                  { month: 'long', day: 'numeric', year: 'numeric' }
                )}
              </time>
              
              <span className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {post.readingTime}
              </span>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert(language === 'en' ? 'Link copied!' : 'Link kopiert!');
                }}
                className="flex items-center hover:text-[var(--brand-primary)] transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.632 8.048c.5.5.866 1.072 1.072 1.707a4.983 4.983 0 011.925-1.925 4.983 4.983 0 01-1.707-1.072c-.434.434-.842.842-1.29 1.29zM12 12v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {language === 'en' ? 'Share' : 'Teilen'}
              </button>
            </div>
          </header>

          {/* Sticky CTA for Mobile */}
          <div className="lg:hidden sticky top-16 z-40 bg-[var(--bg-primary)] border-b border-[var(--border-primary)] -mx-4 px-4 py-3 mb-8">
            <Link 
              to={`/${language}/sign-up`}
              className="block w-full bg-[var(--brand-primary)] text-white text-center px-4 py-2 rounded-lg font-semibold hover:bg-[var(--brand-hover)] transition-all"
            >
              {language === 'en' ? 'Try Free Converter' : 'Kostenlos konvertieren'}
            </Link>
          </div>

          {/* Article Content */}
          <div 
            className="prose prose-invert prose-primary max-w-none prose-lg prose-headings:font-bold prose-headings:text-[var(--text-primary)] prose-p:text-[var(--text-secondary)] prose-a:text-[var(--brand-primary)] prose-a:no-underline hover:prose-a:underline prose-strong:text-[var(--text-primary)] prose-ul:text-[var(--text-secondary)] prose-ol:text-[var(--text-secondary)] prose-blockquote:border-[var(--brand-primary)] prose-blockquote:text-[var(--text-secondary)] prose-code:text-[var(--brand-primary)] prose-code:bg-[var(--bg-secondary)] prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-[var(--bg-secondary)] prose-table:border-[var(--border-primary)]"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Article Footer CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-[var(--brand-primary)]/10 to-[var(--brand-primary)]/5 border border-[var(--brand-primary)]/30 rounded-xl">
            <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              {language === 'en' 
                ? '🚀 Ready to Convert Your Invoices?'
                : '🚀 Bereit, Ihre Rechnungen zu konvertieren?'}
            </h3>
            <p className="text-[var(--text-secondary)] mb-6">
              {language === 'en'
                ? 'Start converting PDF invoices to XRechnung, ZUGFeRD, and other formats today. No credit card required.'
                : 'Beginnen Sie noch heute mit der Konvertierung von PDF-Rechnungen in XRechnung, ZUGFeRD und andere Formate. Keine Kreditkarte erforderlich.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to={`/${language}/sign-up`}
                className="inline-block bg-[var(--brand-primary)] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[var(--brand-hover)] transition-all text-center"
              >
                {language === 'en' ? 'Start Free Trial' : 'Kostenlos testen'}
              </Link>
              <Link 
                to={`/${language}/pricing`}
                className="inline-block bg-[var(--bg-secondary)] text-[var(--text-primary)] px-6 py-3 rounded-lg font-semibold hover:bg-[var(--bg-secondary)]/80 transition-all text-center"
              >
                {language === 'en' ? 'View Pricing' : 'Preise ansehen'}
              </Link>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <section className="mt-16 pt-16 border-t border-[var(--border-primary)]">
          <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-8">
            {language === 'en' ? '📚 Related Articles' : '📚 Verwandte Artikel'}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {relatedPosts.map((relatedPost) => (
              <Link
                key={relatedPost.slug}
                to={`/${language}/blog/${relatedPost.slug}`}
                className="block bg-[var(--bg-secondary)] border border-[var(--border-primary)] rounded-lg p-6 hover:border-[var(--brand-primary)]/40 transition-all group"
              >
                <h3 className="font-bold text-[var(--text-primary)] mb-2 group-hover:text-[var(--brand-primary)] transition-colors line-clamp-2">
                  {relatedPost.title}
                </h3>
                <p className="text-sm text-[var(--text-tertiary)] mb-3 line-clamp-3">
                  {relatedPost.metaDescription}
                </p>
                <span className="text-[var(--brand-primary)] text-sm font-medium inline-flex items-center">
                  {blogTranslations[language].blog.readMore}
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </section>

      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPost;